import { useMemo } from 'react';

import type { TBotConfig } from '~entities/bot';
import { useHasSubscription } from '~features/tags';
import { useGetContacts } from '~hooks/useGetContacts';

import { mapParticipants } from '../model/mapParticipants';
import { sortParticipants } from '../model/sortParticipants';
import type { MappedDialogParticipant } from '../model/types';

export const useDialogs = (
	recipientId?: string
): {
	bots: MappedDialogParticipant[];
	unavailableBots: TBotConfig[];
	nsfwCount: number;
	isLoading: boolean;
	isSuccess: boolean;
	unreadMessagesCount: number;
} => {
	const [hasSubscription] = useHasSubscription();

	const { data, isLoading, isSuccess, isFetching } = useGetContacts({
		pollingInterval: 20 * 60 * 1000,
		recipientId,
	});

	const mappedBots = useMemo(
		() => mapParticipants(data?.contacts ?? {}, hasSubscription),
		[data, hasSubscription]
	);

	const sortedBots = useMemo(() => sortParticipants(mappedBots), [mappedBots]);

	const unreadMessagesCount = useMemo(
		() => mappedBots.reduce((acc, cur) => acc + cur.unreadCount, 0),
		[mappedBots]
	);

	return {
		bots: isFetching ? [] : sortedBots,
		unavailableBots: isFetching
			? []
			: Object.values(data?.inaccessibleContacts ?? {}),
		nsfwCount: data?.nsfwCount || 0,
		isLoading: isLoading || isFetching,
		isSuccess,
		unreadMessagesCount,
	};
};
